import { useState } from "react";

import { DownloadButton } from "./DownloadButton";
import MoreWaysToPlayModal from "./MoreWaysToPlayModal";

import styles from "./DownloadHero.module.scss";

import type { DownloadSection } from "~/typings";
import type { Dictionary } from "~/utils/dictionaries";

import type { MoreWaysToPlayLinks } from "../types";

export default function DownloadHero({
	content,
	dict,
	moreWaysToPlayLinks,
	isAndroid,
	epicClientAndroidInstallerUrl,
}: {
	content: DownloadSection;
	dict: Dictionary;
	moreWaysToPlayLinks: MoreWaysToPlayLinks;
	isAndroid: boolean;
	epicClientAndroidInstallerUrl: string;
}): React.ReactNode {
	const [isMoreWaysToPlayModalOpen, setIsMoreWaysToPlayModalOpen] = useState(false);
	const backgroundImage = content.backgroundImage?.url;
	const appIcon = content.appIcon?.url;
	const qrCode = content.qrCode?.url;
	const contentSubtitle = isAndroid ? content.contentAndroid : content.contentNonAndroid;
	const downloadLink = isAndroid ? epicClientAndroidInstallerUrl : (content.downloadUrlNonAndroid as string);

	return (
		<div className={styles.downloadHero}>
			<div className={styles.heroImgWrapper}>
				<img
					className={styles.heroImg}
					src={backgroundImage!}
					width={1920}
					height={1080}
					alt={content.backgroundImage?.title ?? ""}
				/>
			</div>

			<div className={styles.grid}>
				<div className={styles.heroContentWrapper}>
					<div className={styles.heroBackplate}>
						<div className={styles.backplate} />
						<div className={styles.heroContent}>
							{isAndroid ? (
								<img
									className={styles.appIcon}
									src={appIcon!}
									width={1920}
									height={1080}
									alt={content.appIcon?.title ?? ""}
								/>
							) : null}
							<div className={styles.titleWrapper}>
								<h2 className={styles.sectionTitle}>{dict.hero.title}</h2>
								<p className={styles.sectionCopy}>{contentSubtitle}</p>
							</div>
							<div className={styles.buttonWrapper}>
								<DownloadButton eyebrow={dict.buttons.eyebrow} label={dict.buttons.download} href={downloadLink} />
								<button className={styles.modalBtn} onClick={() => setIsMoreWaysToPlayModalOpen(true)}>
									{dict.morewaystoplay.label}
								</button>
							</div>
						</div>
					</div>
				</div>
				<div className={styles.qrCodeWrapper}>
					<div className={styles.backplate} />
					<div className={styles.qrCodeContent}>
						<img className={styles.qrCode} src={qrCode!} width={1920} height={1080} alt={content.qrCode?.title ?? ""} />
						<p className={styles.sectionCopy}>{content.qrCodeCopy}</p>
					</div>
				</div>
			</div>

			{/*
			 * Kept outside `.heroContentWrapper` (MW-14265): that wrapper sets
			 * `position: relative; z-index: 2`, which opens a stacking context. Nested
			 * inside it, the modal's Bulma `z-index: 40` only ranks within that context,
			 * so the whole subtree stacked at 2 against the navbar's 30 and the nav —
			 * including the burger, which overlaps the fixed close button at top-right —
			 * painted over the modal and swallowed the close tap on mobile.
			 */}
			<MoreWaysToPlayModal
				copy={dict}
				links={moreWaysToPlayLinks}
				isOpen={isMoreWaysToPlayModalOpen}
				setIsOpen={setIsMoreWaysToPlayModalOpen}
			/>
		</div>
	);
}
